import { createContext, ReactNode, useEffect, useRef } from 'react';
import { useAppSelector } from 'src/redux/hook';
import { socket } from 'src/services/socket/socket.io';
import useAuth from 'src/hooks/useAuth';

type Room = {
  userOrganizersRoom?: string;
  userByOrganizerRoom?: string;
  currentOrganizerRoom?: string;
};

type SocketRoomContextType = {
  leaveAllRooms: () => void;
};

const SocketRoomContext = createContext<SocketRoomContextType | null>(null);

type SocketRoomProviderProps = {
  children: ReactNode;
};

export function SocketRoomProvider({ children }: SocketRoomProviderProps) {
  const { user } = useAuth();

  const currentOrganizer = useAppSelector((state) => state.userOrganizers.currentUserOrganizer);

  const oldRoomsRef = useRef<Room>({});
  const userIdRef = useRef<number | undefined>(undefined);
  const organizerIdRef = useRef<number | undefined>(undefined);

  const leaveAllRooms = () => {
    if (oldRoomsRef.current.userOrganizersRoom) {
      socket.emit('leaveRoom', oldRoomsRef.current.userOrganizersRoom);
    }
    if (oldRoomsRef.current.userByOrganizerRoom) {
      socket.emit('leaveRoom', oldRoomsRef.current.userByOrganizerRoom);
    }
    if (oldRoomsRef.current.currentOrganizerRoom) {
      socket.emit('leaveRoom', oldRoomsRef.current.currentOrganizerRoom);
    }
    oldRoomsRef.current = {};
  };

  useEffect(() => {
    userIdRef.current = user?.id;
    organizerIdRef.current = currentOrganizer?.id;

    if (user?.id) {
      const userOrganizersRoom = `user:${user.id}-organizers`;

      socket.emit('joinRoom', {
        newRoom: userOrganizersRoom,
        oldRoom: oldRoomsRef.current.userOrganizersRoom,
      });

      oldRoomsRef.current.userOrganizersRoom = userOrganizersRoom;

      if (currentOrganizer?.id) {
        const userByOrganizerRoom = `user:${user.id}-organizer:${currentOrganizer.id}`;
        const currentOrganizerRoom = `organizer:${currentOrganizer.id}`;

        socket.emit('joinRoom', {
          newRoom: userByOrganizerRoom,
          oldRoom: oldRoomsRef.current.userByOrganizerRoom,
        });
        socket.emit('joinRoom', {
          newRoom: currentOrganizerRoom,
          oldRoom: oldRoomsRef.current.currentOrganizerRoom,
        });

        oldRoomsRef.current.userByOrganizerRoom = userByOrganizerRoom;
        oldRoomsRef.current.currentOrganizerRoom = currentOrganizerRoom;
      }
    }
  }, [user?.id, currentOrganizer?.id]);

  useEffect(() => {
    const joinAllRooms = () => {
      const userId = userIdRef.current;
      const organizerId = organizerIdRef.current;

      if (!userId) return;

      const userOrganizersRoom = `user:${userId}-organizers`;

      socket.emit('joinRoom', { newRoom: userOrganizersRoom });

      oldRoomsRef.current.userOrganizersRoom = userOrganizersRoom;

      if (organizerId) {
        const userByOrganizerRoom = `user:${userId}-organizer:${organizerId}`;
        const currentOrganizerRoom = `organizer:${organizerId}`;

        socket.emit('joinRoom', { newRoom: userByOrganizerRoom });
        socket.emit('joinRoom', { newRoom: currentOrganizerRoom });

        oldRoomsRef.current.userByOrganizerRoom = userByOrganizerRoom;
        oldRoomsRef.current.currentOrganizerRoom = currentOrganizerRoom;
      }
    };

    const handleFocusOrVisibility = () => {
      if (!socket.connected) {
        socket.connect();
      }
    };

    const handleSocketConnect = () => {
      joinAllRooms();
    };

    document.addEventListener('visibilitychange', handleFocusOrVisibility);
    window.addEventListener('focus', handleFocusOrVisibility);

    socket.on('connect', handleSocketConnect);

    return () => {
      document.removeEventListener('visibilitychange', handleFocusOrVisibility);
      window.removeEventListener('focus', handleFocusOrVisibility);

      socket.off('connect', handleSocketConnect);
    };
  }, []);

  return (
    <SocketRoomContext.Provider value={{ leaveAllRooms }}>{children}</SocketRoomContext.Provider>
  );
}

export { SocketRoomContext };
